import type { TOC } from '@ember/component/template-only';
import { Text } from 'tio-ui/components/typography';
import type { ComponentLike } from '@glint/template';
import { getIconTokens, getGradientTokens, getValueTextClass } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// HighlightCard – gradient-background card for benefit/feature highlights
// ---------------------------------------------------------------------------

/**
 * Color theme options for highlight cards
 */
export type HighlightCardColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'indigo'
  | 'pink'
  | 'rose';

interface HighlightCardSignature {
  Args: {
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Prominent value (e.g., "$5,250/year", "AI-Powered") */
    value: string;
    /** Description text */
    description?: string;
    /** Color theme for gradient and icon */
    color?: HighlightCardColor;
  };
  Blocks: {
    /** Optional extra content at the bottom */
    extra?: [];
  };
  Element: HTMLDivElement;
}

const getColorClasses = (color: HighlightCardColor = 'emerald') => {
  const icon = getIconTokens(color, '50');
  const grad = getGradientTokens(color);
  return {
    gradient: grad.gradient,
    border: grad.border,
    iconBg: icon.iconBg,
    iconText: icon.iconText,
    valueText: getValueTextClass(color),
  };
};

/**
 * HighlightCard - A gradient-background card for benefit/feature highlights
 *
 * Used to showcase key benefits or features with a soft gradient background,
 * colored icon badge, title, prominent value, and description.
 *
 * @example
 * ```gts
 * import HighlightCard from 'tio-reimagined/components/highlight-card';
 *
 * <HighlightCard
 *   @icon={{PhGraduationCap}}
 *   @title="Tuition Assistance"
 *   @value="$5,250/year"
 *   @description="Tax-free education funding for degrees and certificates"
 *   @color="emerald"
 * />
 * ```
 */
const HighlightCard: TOC<HighlightCardSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <div
      data-test-highlight-card
      class="bg-gradient-to-br {{colors.gradient}} rounded-xl p-5 border {{colors.border}}"
      ...attributes
    >
      <div class="p-2 {{colors.iconBg}} rounded-lg w-fit mb-3">
        {{#let @icon as |Icon|}}
          <Icon @size={{24}} class={{colors.iconText}} />
        {{/let}}
      </div>
      <div data-test-highlight-card-title class="font-semibold text-gray-900 dark:text-white mb-1">
        {{@title}}
      </div>
      <div data-test-highlight-card-value class="text-2xl font-bold {{colors.valueText}} mb-2">
        {{@value}}
      </div>
      {{#if @description}}
        <Text data-test-highlight-card-description @intent="muted" class="text-sm">
          {{@description}}
        </Text>
      {{/if}}
      {{#if (has-block "extra")}}
        <div class="mt-3">
          {{yield to="extra"}}
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default HighlightCard;

export type { HighlightCardSignature };
